import { FC, useCallback, useEffect, useRef, type PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useChartHeight, useChartWidth } from 'recharts';
import { CHART_CATEGORY_LABEL_GAP, chartLabelColumnWidth, clampChartLabelColumnWidth } from './chartAxis';

type Props = {
  /** Место, отведённое под величины ряда: его граница названий не занимает */
  valueWidth: number;
  /** Доля ширины диаграммы под названия; не задана — граница стоит на отмеренном месте */
  labelWidth?: number;
  /** Новая доля ширины диаграммы под названия — по ходу перетаскивания, раз в кадр */
  onChange: (labelWidth: number) => void;
};

/** Ширина места захвата границы в пикселях — шире видимой черты, чтобы в неё было легко попасть. */
const HANDLE_CATCH_WIDTH = 8;

/** Толщина видимой черты границы в пикселях. */
const HANDLE_LINE_WIDTH = 2;

/** Точность доли ширины: тысячные доли диаграммы — меньше пикселя на любой ширине. */
const SHARE_PRECISION = 1000;

/**
 * Граница между колонкой названий и полосами столбчатой диаграммы по горизонтали: перетаскиванием
 * задаётся ширина колонки названий. Рисуется внутри самой диаграммы — только она знает свою ширину.
 *
 * Черта стоит посередине промежутка между концом названий и началом полос
 * ({@link CHART_CATEGORY_LABEL_GAP}) и не прилегает ни к тем, ни к другим — в том числе по ходу
 * перетаскивания: она отсчитывается от той же ширины колонки, что и сами названия.
 *
 * Ширина держится долей ширины диаграммы: при смене размера диаграммы колонка названий меняется вместе
 * с ней. Пределы — те же, что у показа ({@link clampChartLabelColumnWidth}): полосе остаётся длина,
 * величине — место целиком, а названию — хотя бы начало.
 *
 * Как и растягивание диаграммы, перетаскивание — жест: оно ведётся событиями окна и длится, пока
 * зажата кнопка; ширина показывается раз в кадр.
 */
export const ChartLabelWidthHandle: FC<Props> = ({ valueWidth, labelWidth, onChange }) => {
  const { t } = useTranslation('qualityDashboard');
  const chartWidth = useChartWidth() ?? 0;
  const chartHeight = useChartHeight() ?? 0;
  const width = chartLabelColumnWidth(chartWidth, valueWidth, labelWidth);
  const lineX = width - CHART_CATEGORY_LABEL_GAP / 2;

  // Завершение идущего жеста; жеста нет — `null`
  const finishRef = useRef<(() => void) | null>(null);
  useEffect(() => () => finishRef.current?.(), []);

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<SVGRectElement>) => {
      if (event.button !== 0 || chartWidth <= 0) return;
      finishRef.current?.();
      event.preventDefault();
      event.stopPropagation();
      const start = { pointerX: event.clientX, width };
      let pointerX = event.clientX;
      let frame: number | null = null;

      const showFrame = () => {
        frame = null;
        const nextWidth = clampChartLabelColumnWidth(start.width + pointerX - start.pointerX, chartWidth, valueWidth);
        onChange(Math.round((nextWidth / chartWidth) * SHARE_PRECISION) / SHARE_PRECISION);
      };
      const handleMove = (moveEvent: PointerEvent) => {
        pointerX = moveEvent.clientX;
        frame ??= requestAnimationFrame(showFrame);
      };
      const finish = () => {
        if (frame !== null) {
          cancelAnimationFrame(frame);
          showFrame();
        }
        window.removeEventListener('pointermove', handleMove);
        window.removeEventListener('pointerup', finish);
        window.removeEventListener('pointercancel', finish);
        window.removeEventListener('blur', finish);
        finishRef.current = null;
      };

      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', finish);
      window.addEventListener('pointercancel', finish);
      window.addEventListener('blur', finish);
      finishRef.current = finish;
    },
    [chartWidth, valueWidth, width, onChange],
  );

  return (
    <g role="separator" aria-orientation="vertical" aria-label={t('presets.editor.labelWidth')} className="group/label">
      <rect
        x={lineX - HANDLE_LINE_WIDTH / 2}
        y={0}
        width={HANDLE_LINE_WIDTH}
        height={chartHeight}
        className="fill-primary/40 group-hover/label:fill-primary"
      />
      <rect
        x={lineX - HANDLE_CATCH_WIDTH / 2}
        y={0}
        width={HANDLE_CATCH_WIDTH}
        height={chartHeight}
        className="cursor-col-resize fill-transparent"
        onPointerDown={handlePointerDown}
      />
    </g>
  );
};
